import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import COLORS from '../../../Helpers/utils';

interface ExpandableTextProps {
    /** The text to display. Line breaks are preserved (pre-wrap). */
    text?: string;
    /** Title of the modal that shows the full text; the card's label. */
    title: string;
    /** Fallback shown when there is no text. Defaults to "-". */
    placeholder?: string;
    /** Approx. number of characters to show when collapsed. Defaults to 160. */
    collapsedLength?: number;
}

/**
 * Renders text that may be long. It shows only the first paragraph (or
 * roughly `collapsedLength` characters, whichever is shorter); "Visa mer"
 * opens the full text in a modal, so the card keeps its size.
 */
const ExpandableText: React.FC<ExpandableTextProps> = ({
    text,
    title,
    placeholder = '-',
    collapsedLength = 160,
}) => {
    const [showFull, setShowFull] = useState(false);

    const trimmed = (text ?? '').trim();

    if (!trimmed) {
        return <span style={{ whiteSpace: 'pre-wrap' }}>{placeholder}</span>;
    }

    // Collapse to the first paragraph, but never more than `collapsedLength` chars.
    const firstParagraphEnd = trimmed.indexOf('\n');
    let cutoff = firstParagraphEnd === -1 ? trimmed.length : firstParagraphEnd;
    cutoff = Math.min(cutoff, collapsedLength);

    const needsToggle = cutoff < trimmed.length;
    const collapsedText = needsToggle ? `${trimmed.slice(0, cutoff).trimEnd()}…` : trimmed;

    return (
        <span style={{ whiteSpace: 'pre-wrap' }}>
            {collapsedText}
            {needsToggle && (
                <>
                    {' '}
                    <button
                        type="button"
                        onClick={() => setShowFull(true)}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: COLORS.primary,
                            cursor: 'pointer',
                            font: 'inherit',
                            textDecoration: 'underline',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Visa mer
                    </button>
                    <Modal show={showFull} onHide={() => setShowFull(false)} centered scrollable>
                        <Modal.Header closeButton>
                            <Modal.Title>{title}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>{trimmed}</Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowFull(false)}>Stäng</Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </span>
    );
};

export default ExpandableText;
